import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  FilterModel,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SortModelItem,
} from 'ag-grid-community';
import { format } from 'date-fns';

import { createShipmentOptions } from '@/services/shipment-service/hooks';
import { addGridQueryParams } from '@/utils/query';

export type ShipmentFilters = {
  search?: string;
  date?: Date;
  needByDate?: Date;
  shipToName?: string;
  shipName?: string;
  tank?: string;
  trackingNumber?: string;
  comment?: string;
  amount?: number;
  shippingMethod?: number;
  shipmentStatus?: number;
  shipmentNumber?: string;
  ownerName?: string;
  skip?: number;
  take?: number;
  order?: SortModelItem[];
};

export const useShipmentsDataSource = (filters: ShipmentFilters) => {
  const queryClient = useQueryClient();
  const {
    search,
    date,
    needByDate,
    shipToName,
    shipName,
    tank,
    trackingNumber,
    comment,
    amount,
    shippingMethod,
    shipmentStatus,
    shipmentNumber,
    ownerName,
    order,
  } = filters;

  const getRows = useCallback(
    (params: IServerSideGetRowsParams) => {
      if (
        typeof params.request.startRow !== 'number' ||
        typeof params.request.endRow !== 'number'
      ) {
        return;
      }

      const searchParams = new URLSearchParams();

      if (search) searchParams.set('search', search);
      if (date) searchParams.set('date', date.toISOString().split('T')[0]);
      if (needByDate)
        searchParams.set('needByDate', format(needByDate, 'yyyy-MM-dd'));
      if (shipToName) searchParams.set('shipToName', shipToName);
      if (shipName) searchParams.set('shipName', shipName);
      if (tank) searchParams.set('tank', tank);
      if (trackingNumber) searchParams.set('trackingNumber', trackingNumber);
      if (comment) searchParams.set('comment', comment);
      if (amount !== undefined) searchParams.set('amount', amount.toString());
      if (shippingMethod !== undefined)
        searchParams.set('shippingMethod', shippingMethod.toString());
      if (shipmentStatus !== undefined)
        searchParams.set('shipmentStatus', shipmentStatus.toString());
      if (shipmentNumber) searchParams.set('shipmentNumber', shipmentNumber);
      if (ownerName) searchParams.set('ownerName', ownerName);

      const sortModel =
        params.request.sortModel.length > 0
          ? params.request.sortModel
          : (order ?? []);

      queryClient
        .fetchQuery(
          createShipmentOptions(
            addGridQueryParams(
              searchParams,
              params.request.filterModel as FilterModel | null,
              sortModel,
              params.request.startRow,
              params.request.endRow,
            ),
          ),
        )
        .then((response) => {
          params.success({
            rowData: response.items,
            rowCount: response.totalCount,
          });
        })
        .catch(() => {
          params.fail();
        });
    },
    [
      queryClient,
      search,
      date,
      needByDate,
      shipToName,
      shipName,
      tank,
      trackingNumber,
      comment,
      amount,
      shippingMethod,
      shipmentStatus,
      shipmentNumber,
      ownerName,
      order,
    ],
  );

  const dataSource: IServerSideDatasource = useMemo(
    () => ({ getRows }),
    [getRows],
  );

  return { dataSource };
};
